import { CAPSTONE } from '@rsos/constants/appNames';
import { gspx } from '@rsos/gainsight';
import { fetchVideoSessions } from '@rsos/reduxDataStore';
import { fetchAlerts } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { fetchPsap } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import { fetchLanguageList } from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import {
  fetchUser,
  postAuthTokenSuccess,
  selectUserOrgByApp,
  setTokens,
  sessionStart,
} from '@rsos/sinatra';
import { getUrlParameter } from '@rsos/utils';
import { getRspAlertQueueMaxSize } from '@rsos/utils/metaTags';
import { configureUserScope } from '@rsos/utils/sentry';
import canViewAlerts from '../utils/canViewAlerts';
import {
  APP_INIT_START,
  APP_INIT_SUCCESS,
  APP_INIT_FAILED,
} from './actionTypes';

const RSP_ALERT_QUEUE_MAX_SIZE = getRspAlertQueueMaxSize();

export const appInitStart = () => ({
  type: APP_INIT_START,
});

export const appInitSuccess = () => ({
  type: APP_INIT_SUCCESS,
});

export const appInitFailed = error => ({
  type: APP_INIT_FAILED,
  error,
});

const hardRedirect = to => {
  window.location = to;
};

const initialize = () => (dispatch, store) => {
  dispatch(appInitStart());

  // If using the auto login/query feature, parse the token and set the
  // token to localStorage and the store to be used if user navigates to
  // another app
  const isLoggedIn = store().sinatra.user.isLoggedIn;
  const accessToken = getUrlParameter('access_token');
  const {
    sinatra: {
      irp: { irp_access_token },
    },
  } = store();

  const token = irp_access_token ? irp_access_token : accessToken;

  if (token) {
    const tokens = {
      refresh_token: null,
      token: token,
    };
    setTokens(tokens);
    dispatch(postAuthTokenSuccess(tokens));
    dispatch(sessionStart());
    gspx.trackCustomEvent('Auth Session', {
      name: 'Session Start from query params',
      Category: 'Session tracking',
      'Launched date': new Date(),
    });
  }

  return dispatch(fetchUser())
    .then(user => {
      if (user && user.profile) {
        configureUserScope(user.profile);
      }
    })
    .then(() => {
      const { sinatra } = store();
      const org = selectUserOrgByApp(sinatra, CAPSTONE);
      return dispatch(fetchPsap(`/psaps/${org.name}?invert_polygons=true`));
    })
    .then(() => {
      const initializationPromises = [];
      const { psaps, sinatra } = store();
      const orgName = sinatra.user.currentOrg.name;
      const hasAlertsAccess = canViewAlerts(sinatra, psaps);
      if (hasAlertsAccess) {
        initializationPromises.push(
          dispatch(
            fetchAlerts({
              orgName,
              queueMaxSize: RSP_ALERT_QUEUE_MAX_SIZE,
              endTime: new Date().getTime(),
            }),
          ),
        );
      }
      initializationPromises.push(
        dispatch(fetchVideoSessions()).then(result => {
          if (result?.payload === 'Video to 911 Core is not enabled.') {
            // Ignore this error, action is rejected if new video-to-911
            // is not enabled.
            return;
          } else {
            return result;
          }
        }),
      );
      initializationPromises.push(dispatch(fetchLanguageList(orgName)));
      return Promise.all(initializationPromises);
    })
    .then(() => dispatch(appInitSuccess()))
    .catch(action => {
      // eslint-disable-next-line no-console
      console.error('App initialization failed', action.error || action);

      // action can either be action with an error or a JS error.
      if (isLoggedIn) {
        return dispatch(appInitFailed(action.error || action));
      }

      return hardRedirect('/');
    });
};

export default initialize;
